/**
 * The three states every screen owes the reader: loading, error, empty.
 * Nothing in this app is allowed to render a blank region.
 */

export function BootScreen() {
  return (
    <div className="boot">
      <div className="boot__mark" aria-hidden="true" />
      <p className="boot__text">
        <Spinner />
        Checking your session…
      </p>
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <>
      <span className="spinner" aria-hidden="true" />
      {label ? <span>{label}</span> : null}
    </>
  );
}

export function LoadingState({ label = 'Loading…', rows = 3 }) {
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <p className="state__line">
        <Spinner />
        {label}
      </p>
      <div className="skeleton" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <span key={index} className="skeleton__bar" style={{ width: `${100 - index * 14}%` }} />
        ))}
      </div>
    </div>
  );
}

export function ErrorState({ error, onRetry, title = 'Could not load this view' }) {
  const message =
    (error && error.message) || 'The request failed and the server gave no reason.';
  const status = error && error.status ? error.status : null;

  return (
    <div className="state state--error" role="alert">
      <p className="state__title">
        <StatusGlyph shape="diamond" tone="critical" />
        {title}
      </p>
      <p className="state__body">{message}</p>
      {status ? <p className="state__meta mono">HTTP {status}</p> : null}
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title = 'Nothing here yet', body, action }) {
  return (
    <div className="state state--empty">
      <p className="state__title">{title}</p>
      {body ? <p className="state__body">{body}</p> : null}
      {action}
    </div>
  );
}

/**
 * The shape channel for status. Colour is the fast read; the shape is what
 * survives deuteranopia, greyscale printing and forced-colors mode. Always
 * rendered next to a text label — never on its own.
 */
export function StatusGlyph({ tone = 'neutral', shape = 'disc' }) {
  return <span className={`glyph glyph--${tone} glyph--${shape}`} aria-hidden="true" />;
}

/**
 * Renders `children`, or one of the three states. Used by every screen so the
 * loading/error/empty contract is impossible to forget.
 */
export function Resource({
  state,
  isEmpty,
  emptyTitle,
  emptyBody,
  loadingLabel,
  loadingRows,
  errorTitle,
  children,
}) {
  if (state.status === 'loading' && state.data == null) {
    return <LoadingState label={loadingLabel} rows={loadingRows} />;
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} onRetry={state.refresh} title={errorTitle} />;
  }
  if (state.data == null) {
    return <EmptyState title={emptyTitle} body={emptyBody} />;
  }
  if (isEmpty && isEmpty(state.data)) {
    return <EmptyState title={emptyTitle} body={emptyBody} />;
  }
  return children(state.data);
}
